import * as d3 from "d3";
import { DateTime } from "luxon";
import { groupBy, pct, getQueryVariable, getOffset, getLastItem } from "./utils";
import { loadMutationsData, loadLineagesData } from "./data";
import * as Events from "./events";
import { Mutation } from "./sequtils";
import { openPdf, initPdf } from "./pdf";
import * as Export from "./export";
import { getCountryDetails, getProvinceDetails, getCountryId, getProvinceId } from "./locations";
import {
  CHART_DATE_FORMAT,
  CHART_DATE_FORMAT_MONTH_SHORT,
  CHART_DATE_FORMAT_DATE_SHORT,
  CHART_DATE_FORMAT_YEAR_SHORT,
  DATE_FORMAT_CUSTOM_VALUE,
  MAX_MUTATIONS,
  MAX_LINEAGES,
  MAX_COUNTRIES,
  MIN_PCT,
  MIN_CHARS_MUTATIONS_SEARCH,
  NUM_RESULTS_FOR_EACH_GENE_REGION,
  ADDL_RESULTS_FOR_EACH_GENE_REGION,
  MIN_CHARS_COUNTRIES_SEARCH,
  UNCERTAIN_THRESHOLD,
  PROVINCE_SEPARATOR
} from "./constants";

let darkMode = false;
let overlappingMutationsToggle = false;

let gridMode = false;

let processedData;
// Set defaults -- these are only used for first-time users with no localStorage, or URL passed
let countries = ['USA'], countriesByRegion, provincesByCountry;
let mutations = ['S:R346K', 'S:L452Q'], mutationsByGeneRegion;
let defaultMutations = [...mutations];
let lineages = ['BA.4.6', 'BA.2.75'];
let defaultLineages = [...lineages];
let timeframe = '4months', frequency;

let allMutations, allLineages, allCountries;

let startDate, endDate, mostRecentDataDate, updateDate, currentHighlightDate;
let earliestDataDate = DateTime.fromISO("2019-12-31");

let svgOffset = 0;

let dataMode = "mutations"; // "lineages"
let defaultDataMode = "mutations";

let x, y;

const localStorage = window.localStorage;
//localStorage.clear(); // uncomment to reset localStorage

import geographyUrl from './data/geography.json?url';



let hideLoader = ()=>{
  document.body.classList.remove('loading');
};
let showLoader = ()=>{
  document.body.classList.add('loading');
};
let updateLoaderMessage = (msg) => {
  document.querySelector("#loading-message").innerHTML = msg;
}
let enterDarkMode = ()=>{
  document.body.classList.add('night');
  darkMode = true;
};
let exitDarkMode = ()=>{
  document.body.classList.remove('night');
  darkMode = false;
};




// Initialization

initialize();

function initialize() {

  showLoader();

  let dataModeLoaded = loadDataModeFromQueryString();
  if (!dataModeLoaded) {
    dataModeLoaded = loadDataModeFromLocalStorage();
  }

  let mutationsLoaded = loadMutationsFromQueryString();
  if (!mutationsLoaded) {
    mutationsLoaded = loadMutationsFromLocalStorage();
  }

  let lineagesLoaded = loadLineagesFromQueryString();
  if (!lineagesLoaded) {
    lineagesLoaded = loadLineagesFromLocalStorage();
  }

  let countriesLoaded = loadCountriesFromQueryString();
  if (!countriesLoaded) {
    countriesLoaded = loadCountriesFromLocalStorage();
  }

  let overlapMutationsLoaded = loadOverlapMutationsFromQueryString();
  if (!overlapMutationsLoaded) {
    overlapMutationsLoaded = loadOverlapMutationsFromLocalStorage();
  }

  let timeframeLoaded = loadTimeframeFromQueryString();
  if (!timeframeLoaded) {
    timeframeLoaded = loadTimeframeFromLocalStorage();
  }




  if (dataMode === "mutations") {
    document.body.classList.toggle("mutations", true);
    document.body.classList.toggle("lineages", false);

    updateMutationSelections();
    updateOverlappingMutations();
  } else if (dataMode === "lineages") {
    document.body.classList.toggle("mutations", false);
    document.body.classList.toggle("lineages", true);

    updateLineagesSelections();
  }



  closeMutationsSearch();
  closeLineagesSearch();
  closeCountriesSearch();
  addEvents();
  checkMaxCountries();
  checkMaxMutations();
  checkMaxLineages();
  fetchData();

  loadCharts();

}

function loadDataModeFromLocalStorage() {
  let dm = localStorage.getItem('datamode');
  if (dm) {
    dataMode = JSON.parse(dm);
    if (!dataMode) {
      dataMode = defaultDataMode;
    }
    return true;
  }
  return false;
}
function loadMutationsFromLocalStorage() {
  let m = localStorage.getItem('mutations');
  if (m) {
    mutations = JSON.parse(m);
    if (mutations.length === 0) {
      mutations = [...defaultMutations];
    }
    return true;
  }
  return false;
}
function loadLineagesFromLocalStorage() {
  let l = localStorage.getItem('lineages');
  if (l) {
    lineages = JSON.parse(l);
    if (lineages.length === 0) {
      lineages = [...defaultLineages];
    }
    return true;
  }
  return false;
}
function loadCountriesFromLocalStorage() {
  let c = localStorage.getItem('countries');
  if (c) {
    countries = JSON.parse(c);
    return true;
  }
  return false;
}
function loadTimeframeFromLocalStorage() {
  let t = localStorage.getItem('timeframe');
  if (t) {
    setTimeframe(JSON.parse(t));
    return true;
  }
  return false;
}
function loadOverlapMutationsFromLocalStorage() {
  let o = localStorage.getItem('overlaps');
  if (o) {
    overlappingMutationsToggle = JSON.parse(o);
    return true;
  }
  return false;
}


function setLocalStorage() {
  localStorage.setItem('datamode', JSON.stringify(dataMode));
  localStorage.setItem('mutations', JSON.stringify(mutations));
  localStorage.setItem('lineages', JSON.stringify(lineages));
  localStorage.setItem('countries', JSON.stringify(countries));
  localStorage.setItem('timeframe', JSON.stringify(getTimeframe()));
  localStorage.setItem('overlaps', JSON.stringify(overlappingMutationsToggle));

  document.querySelectorAll(".deeplink").forEach(link => link.classList.toggle("copied", false));
  document.querySelector("#deeplink-activator p").innerHTML = "Copy link";

  document.querySelectorAll(".download-pdf").forEach(link => link.classList.toggle("downloaded", false));
  document.querySelectorAll(".download-pdf p").forEach(link => link.innerHTML = "Download PDF of this view");
}

function clearLocalStorage() {

  localStorage.removeItem('datamode');
  localStorage.removeItem('mutations');
  localStorage.removeItem('lineages');
  localStorage.removeItem('countries');
  localStorage.removeItem('timeframe');
  localStorage.removeItem('overlaps');

  console.log('localStorageCleared');

}

function loadDataModeFromQueryString() {
  let queryDataMode = getQueryVariable("datamode");
  if (queryDataMode) {
    dataMode = queryDataMode;
    return true;
  }
  return false;
}
function loadMutationsFromQueryString() {
  let queryMutations = getQueryVariable("mutations");
  if (queryMutations) {
    mutations = queryMutations.split(',');
    return true;
  }
  return false;
}
function loadLineagesFromQueryString() {
  let queryLineages = getQueryVariable("lineages");
  if (queryLineages) {
    lineages = queryLineages.split(',');
    return true;
  }
  return false;
}
function loadCountriesFromQueryString() {
  let queryCountries = getQueryVariable("countries");
  if (queryCountries) {
    countries = queryCountries.split(',');
    return true;
  } else if (queryCountries === '') {
    countries = [];
    return true;
  }
  return false;
}
function loadTimeframeFromQueryString() {
  let queryTimeframe = getQueryVariable("timeframe");
  if (queryTimeframe) {
    setTimeframe(JSON.parse(queryTimeframe));
    return true;
  }
  return false;
}
function loadOverlapMutationsFromQueryString() {
  let queryOverlaps = getQueryVariable("overlaps");
  if (queryOverlaps) {
    overlappingMutationsToggle = JSON.parse(queryOverlaps);
    return true;
  }
  return false;
}

function fetchData(loadMutationsSearch) {

  Promise.all([
    fetch(`https://wopr.fathom.info/datadict/mutation/`)
      .then(res => res.json())
      .then(data => {
        allMutations = data.mutation;

        mutationsByGeneRegion = processMutations(allMutations);

        if (loadMutationsSearch) {
          hideLoader();

          updateMutationSelections();
          openMutationsSearch();
        }
      })
      .catch(err => {
        handleError(err);
      }),
    fetch(`https://wopr.fathom.info/datadict/lineage/`)
      .then(res => res.json())
      .then(data => {
        allLineages = data.lineages;
      })
      .catch(err => {
        handleError(err);
      })
  ])


  // Pre-fetch countries

  fetch(`https://wopr.fathom.info/datadict/country/`)
    .then(res => res.json())
    .then(data => {
      allCountries = data.country;

      fetch(geographyUrl)
        .then(res => res.json())
        .then(data => {
          countriesByRegion = data.WorldGeography;
          provincesByCountry = data.StateGeography;

          processCountries(allCountries, countriesByRegion);
        })
        .catch(err => {
          handleError(err);
        });
    })
    .catch(err => {
      handleError(err);
    });

  // Pre-Fetch update date, needed to calculate chart filter dates as well
  fetch(`https://wopr.fathom.info/metadata/`)
    .then(res => res.json())
    .then(data => {
      updateDate = DateTime.fromISO(data.mutations);
      setUpdateDate(updateDate);
    })
    .catch(err => {
      handleError(err);
    });

}

function addEvents() {

  // Mutation / Lineages Toggle
  let toggleContainer = document.querySelector("#toggle");
  // toggleContainer.querySelectorAll(".toggle-opt").forEach(toggle => {
  //   toggle.addEventListener("click", () => {
  //     let toggleType = toggle.getAttribute("data-toggle");

  //     document.body.classList.toggle("mutations", false);
  //     document.body.classList.toggle("lineages", false);

  //     document.body.classList.toggle(toggleType, true);

  //     dataMode = toggleType;

  //     if (dataMode === "mutations") {
  //       updateMutationSelections();
  //       loadCharts();
  //     } else if (dataMode === "lineages") {
  //       updateLineagesSelections();
  //       loadCharts();
  //     }
  //   });
  // });
  toggleContainer.querySelector("#toggle-elements").addEventListener("click", () => {
    let toggleType = "";
    if (document.body.classList.contains("mutations")) {
      toggleType = "lineages";
    } else if (document.body.classList.contains("lineages")) {
      toggleType = "mutations";
    }
    document.body.classList.toggle("mutations", false);
    document.body.classList.toggle("lineages", false);
    document.body.classList.toggle(toggleType, true);
    dataMode = toggleType;
    if (dataMode === "mutations") {
      updateMutationSelections();
      updateOverlappingMutations();
      loadCharts();
    } else if (dataMode === "lineages") {
      updateLineagesSelections();
      loadCharts();
    }
  });
  toggleContainer.querySelector(".toggle-track").addEventListener("click", () => {
    if (document.body.classList.contains("mutations")) {
      document.body.classList.toggle("mutations", false);
      document.body.classList.toggle("lineages", true);

      dataMode = "lineages";

      updateLineagesSelections();
      loadCharts();
    } else {
      document.body.classList.toggle("mutations", true);
      document.body.classList.toggle("lineages", false);

      dataMode = "mutations";

      updateMutationSelections();
      updateOverlappingMutations();
      loadCharts();
    }
  });



  // Searching

  let searchAccess = document.querySelector("#selector--search--access");
  let mutationSearchInput = document.querySelector("#mutation-search--input");
  let lineagesSearchInput = document.querySelector("#lineage-search--input");

  let mutationSearchInputTimeout;

  // The 'input' event picks up keypresses, but also picks up the user hitting the cancel button
  mutationSearchInput.addEventListener("input", () => {
    clearTimeout(mutationSearchInputTimeout);
    let value = mutationSearchInput.value;
    if (value === '' || value.length >= MIN_CHARS_MUTATIONS_SEARCH) {
      mutationSearchInputTimeout = setTimeout(() => mutationSearch(value), 500);
    }

    if (value !== '') {
      mutationSearchInput.classList.toggle("selected", true);
    } else {
      mutationSearchInput.classList.toggle("selected", false);
    }
  });

  let lineagesSearchInputTimeout;

  // The 'input' event picks up keypresses, but also picks up the user hitting the cancel button
  lineagesSearchInput.addEventListener("input", () => {
    clearTimeout(lineagesSearchInputTimeout);
    let value = lineagesSearchInput.value;
    if (value === '' || value.length >= MIN_CHARS_MUTATIONS_SEARCH) {
      lineagesSearchInputTimeout = setTimeout(() => lineagesSearch(value), 500);
    }

    if (value !== '') {
      lineagesSearchInput.classList.toggle("selected", true);
    } else {
      lineagesSearchInput.classList.toggle("selected", false);
    }
  });

  // Mutation access…
  searchAccess.addEventListener("click", () => {
    if (dataMode === "mutations") {
      if (!isMutationsSearchOpen()) {
        openMutationsSearch();
      }
    } else if (dataMode === "lineages") {
      if (!isLineagesSearchOpen()) {
        openLineagesSearch();
      }
    }
  });
  searchAccess.addEventListener("blur", () => {
    searchAccess.classList.toggle("selected", false);
  });

  // Mutation done button
  document.querySelectorAll(".search--dismisser").forEach(button => {
    button.addEventListener("click", () => {
      closeMutationsSearch();
      closeLineagesSearch();
      closeCountriesSearch();
      loadCharts();
    });
  });


  // Toggle overlapping mutations
  document.querySelector("#show-overlap").addEventListener("input", () => {
    overlappingMutationsToggle = !overlappingMutationsToggle; // toggle it
    if (overlappingMutationsToggle) {
      document.body.classList.toggle("overlapping", true);
    } else {
      document.body.classList.toggle("overlapping", false);
    }
    loadCharts();
    setLocalStorage();
  });


  // Global listeners

  // Date selection and any modal cleanups
  // If the date menu is open and you click anywhere outside of it, close it
  document.body.addEventListener("click", (e) => {
    let parentNode = document.querySelector("#selector--dates");
    if (parentNode.classList.contains("open")) {
      if (!parentNode.contains(e.srcElement)) {
        document.querySelector("#selector--dates").classList.toggle("open", false);
      }
    }

    // disable the source message as well
    parentNode = document.querySelector("#source--gisaid");
    if (!parentNode.contains(e.srcElement)) {
      document.querySelector("#source--gisaid").classList.toggle("show", false);
    }

    // disable the info note as well
    parentNode = document.querySelector("#info-activator");
    if (!parentNode.contains(e.srcElement)) {
      document.querySelector("#info-activator").classList.toggle("show", false);
    }
  });

  // Resize

  window.addEventListener("resize", () => {
    let data = getDataCopy();
    if (data) {
      data = filterData(data);
      resize(data);
    }
  });

  // Have to hit both of these elements, and not the parent #selector--dates, which is parent to other clickable elements with different behaviors
  document.querySelector("#selector--date--symbol").addEventListener("click", () => {
    document.querySelector("#selector--dates").classList.toggle("open", true);
  });
  document.querySelector("#selector--date--selection").addEventListener("click", () => {
    document.querySelector("#selector--dates").classList.toggle("open", true);
  });

  document.querySelectorAll("#selector--date--menu--options li[data-timeframe]").forEach(li => {
    li.addEventListener("click", () => {

      document.querySelector("#selector--dates").classList.toggle("open", false);

      // Disable all of them
      document.querySelectorAll("#selector--date--menu--options li").forEach(_li => _li.classList.toggle("active", false));
      li.classList.toggle("active", true);

      // Clear errors
      document.querySelector("#selector--date--custom--errors").innerHTML = "";
      document.querySelector("#selector--date--custom--errors").classList.toggle("hidden", true);

      // Set the value to power the charts
      //setTimeframe(li.getAttribute("data-timeframe"));
      setTimeframeFromUI();
      updateTimeframeSelection();

      // Set the label
      // let timeframeLabel = li.innerHTML;
      // document.querySelector("#selector--date--selection").innerHTML = `${ timeframeLabel }`;

      let data = getDataCopy();
      data = filterData(data);
      draw(data);

    });
  });
  document.querySelector("#selector--date--menu .custom").addEventListener("click", () => {
    // Disable all of them
    document.querySelectorAll("#selector--date--menu--options li").forEach(_li => _li.classList.toggle("active", false));

    // Enable custom
    document.querySelector("#selector--date--menu .custom").classList.toggle("active", true);
    document.querySelector("#selector--date--custom").classList.toggle("active", true);

    document.querySelector("#selector--date--custom--date-start").setAttribute("max", updateDate.toISODate());
    document.querySelector("#selector--date--custom--date-end").setAttribute("max", updateDate.toISODate());

  });



  document.querySelector("#selector--date--custom--go").addEventListener("click", () => {

    let dateStartInput = document.querySelector("#selector--date--custom--date-start");
    let dateEndInput = document.querySelector("#selector--date--custom--date-end");

    if (dateStartInput.value !== '' && dateEndInput.value !== '') {

      // console.log(dateStartInput.value);
      // console.log(DateTime.fromISO(dateStartInput.value).toISODate());
      // console.log(earliestDataDate.toISODate());

      let checkStartDate = DateTime.fromISO(dateStartInput.value);
      let checkEndDate = DateTime.fromISO(dateEndInput.value);



      let dateErrorMessage;

      if (checkStartDate.invalid) {
        dateErrorMessage = 'Start date is not a valid date';
      }
      if (checkEndDate.invalid) {
        dateErrorMessage = `End date is not a valid date`;
      }

      if (checkStartDate.diff(earliestDataDate, 'days').days < 0) {
        dateErrorMessage = `Start date must be after ${  earliestDataDate.toFormat(CHART_DATE_FORMAT)}`;
      }

      if (checkStartDate.diff(checkEndDate, 'days').days > 0) {
        dateErrorMessage = `Start date must be before end date`;
      }

      if (checkEndDate.diff(earliestDataDate, 'days').days < 0) {
        dateErrorMessage = `End date must be after ${  earliestDataDate.toFormat(CHART_DATE_FORMAT)}`;
      }


      if (dateErrorMessage) {

        document.querySelector("#selector--date--custom--errors").innerHTML = dateErrorMessage;
        document.querySelector("#selector--date--custom--errors").classList.toggle("hidden", false);

      } else {

        document.querySelector("#selector--date--custom--errors").innerHTML = "";
        document.querySelector("#selector--date--custom--errors").classList.toggle("hidden", true);

        document.querySelector("#selector--dates").classList.toggle("open", false);

        setTimeframeFromUI();
        updateTimeframeSelection();

        let data = getDataCopy();
        data = filterData(data);
        draw(data);
      }

    }
  });


  // Close countries
  document.querySelector("#prompt .search--dismisser").addEventListener("click", () => {
    closeMutationsSearch();
    closeCountriesSearch();
    loadCharts();
  });

  // Country searching
  document.querySelector("#country-search--input").addEventListener("focus", () => {
    if (!isCountriesSearchOpen()) {
      // openCountriesSearch();
      closeMutationsSearch();
    }
  });
  let countrySearchInputTimeout;
  document.querySelector("#country-search--input").addEventListener("keyup", () => {
    clearTimeout(countrySearchInputTimeout);
    let value = document.querySelector("#country-search--input").value;
    if (isCountriesSearchOpen()) {
      if (value === '' || value) {
        countrySearchInputTimeout = setTimeout(() => countrySearch(value), 250);
      }
    } else {
      if (value === '' || value.length >= MIN_CHARS_COUNTRIES_SEARCH) {
        countrySearchInputTimeout = setTimeout(() => countrySearch(value), 250);
      }
    }
  });



  document.querySelector("#source--gisaid").addEventListener("click", () => {
    document.querySelector("#source--gisaid").classList.toggle("show", true);
  });

  document.querySelector("#info-activator").addEventListener("click", () => {
    document.querySelector("#info-activator").classList.toggle("show", true);
  });

  // Dark mode / light mode
  // document.getElementById('night-activator').addEventListener('click', enterDarkMode);
  // document.getElementById('day-activator').addEventListener('click', exitDarkMode);

  document.querySelectorAll(".deeplink").forEach(link => {
    link.addEventListener("click", async () => {
      //await navigator.clipboard.writeText(window.location.href);

      const url = new URL(window.location);
      url.searchParams.set('datamode', dataMode);
      url.searchParams.set('mutations', mutations.join(','));
      url.searchParams.set('lineages', lineages.join(','));
      url.searchParams.set('countries', countries.join(','));
      url.searchParams.set('timeframe', JSON.stringify(getTimeframe()));
      url.searchParams.set('overlaps', JSON.stringify(overlappingMutationsToggle));

      await navigator.clipboard.writeText(url);

      link.classList.toggle("copied", true);
      link.querySelector("p").innerHTML = "Link copied";

    });
    link.addEventListener("touchstart", async () => {
      //await navigator.clipboard.writeText(window.location.href);

      const url = new URL(window.location);
      url.searchParams.set('datamode', dataMode);
      url.searchParams.set('mutations', mutations.join(','));
      url.searchParams.set('lineages', lineages.join(','));
      url.searchParams.set('countries', countries.join(','));
      url.searchParams.set('timeframe', JSON.stringify(getTimeframe()));
      url.searchParams.set('overlaps', JSON.stringify(overlappingMutationsToggle));

      await navigator.clipboard.writeText(url);

      link.classList.toggle("copied", true);
      link.querySelector("p").innerHTML = "Link copied";

    }, {passive:true});

  });

  document.querySelectorAll(".download-pdf").forEach(link => {

    return; // Remove this to enable pdf functionality


    link.addEventListener("click", async () => {
      // Do pdf things here

      link.classList.toggle("downloaded", true);
      link.querySelector("p").innerHTML = "Downloaded";

    });

  });

  document.querySelector("#snapshot-activator").addEventListener("click", async () => {

    let data = getDataCopy();
    data = filterData(data);

    initPdf(
      data,
      resize,
      updateDate,
      startDate,
      endDate,
      showLoader,
      hideLoader,
      updateLoaderMessage
    );

    openPdf();
  });


  // Chart hover -- attached to the parent element
  document.querySelector("#charter").addEventListener("mousemove", e => {

    let xCoord = e.clientX;
    let offsetXCoord = xCoord - svgOffset.left;
    Events.emit('dateHighlight', offsetXCoord);

  });

  document.querySelector("#charter").addEventListener("click", e => {
    Export.hideDialog();
  });

  // Chart Right-click -> Export lineages for mutations (only on mutations view)
  document.querySelector("#charter").addEventListener("contextmenu", e => {

    if (dataMode === "mutations") {
      e.preventDefault();

      let xCoord = e.clientX;
      let yCoord = e.clientY;
      let offsetXCoord = xCoord - svgOffset.left;
      let offsetYCoord = yCoord - svgOffset.top;

      let isWeekly = false;
      if (getFrequency() === "weekly") {
        isWeekly = true;
      }

      let date = currentHighlightDate;

      Export.showDialog(mutations, countries, overlappingMutationsToggle, date, isWeekly, offsetXCoord, yCoord);
    }
  });

  Export.addEvents();

  // Mobile flavor

  document.addEventListener('touchmove', (event)=> {
    if (event.scale !== 1) event.preventDefault(); //if a scale gesture, don't do it
  });

  document.querySelector("#charter").addEventListener("touchstart", e => {

    //e.preventDefault();

    if (e.touches.length > 0) {

      let touch = e.touches[0];

      let xCoord = touch.clientX;
      let offsetXCoord = xCoord - svgOffset.left;

      Events.emit('dateHighlight', offsetXCoord);

    }

  }, {passive:true});

  document.querySelector("#charter").addEventListener("touchmove", e => {

    //e.preventDefault();

    if (e.touches.length > 0) {

      let touch = e.touches[0];

      let xCoord = touch.clientX;
      let offsetXCoord = xCoord - svgOffset.left;

      Events.emit('dateHighlight', offsetXCoord);

    }

  }, {passive:true});


  document.querySelector("#clear-local-storage").addEventListener("click", () => {
    clearLocalStorage();
    location.reload();
  });


}


function setUpdateDate(updateDate) {
  document.querySelector("#last-updated").innerHTML = updateDate.toFormat(CHART_DATE_FORMAT);
}
function setMostRecentDate(mostRecentDate) {
  document.querySelector("#most-recent").innerHTML = mostRecentDate.toFormat(CHART_DATE_FORMAT);
}

// Mutations
// =======================


function openMutationsSearch(searchValue) {

  document.querySelector("#content").classList.toggle("searching-mutations", true);
  document.querySelector("#mutation-search--input").placeholder = "Search mutations";
  document.querySelector("#mutation-search--input").focus();

  // Hide the mutations dismisser if there are no mutations
  if (mutations.length === 0) {
    document.querySelector("#mutations .search--dismisser").classList.toggle("hidden", true);
  } else {
    document.querySelector("#mutations .search--dismisser").classList.toggle("hidden", false);
  }

  let container = document.querySelector("#mutation-search-contents");

  mutationsByGeneRegion.forEach(geneRegion => {
    geneRegion.resultsToShow = NUM_RESULTS_FOR_EACH_GENE_REGION;
  });

  //container.innerHTML = matchesHtml + html;
  if (searchValue) {
    runMutationsSearch(searchValue, container);
  } else if (searchValue === '') {
    runMutationsSearch(searchValue, container);
  }



}
function runMutationsSearch(searchValue, container, expandedGeneRegion) {
  //if (!noData) {

  let resultsHtml = ``;
  let matches = 0;

  let re, reLabel;
  if (searchValue || searchValue === '') {
    // Handle spike to S: wildcard
    let matchValue = searchValue.replace(/spike/gi, 'S:');


    // Handle / to - for deletion mutations
    matchValue = matchValue.replace(/\//gi, '-');

    matchValue = matchValue.replace(/\\/gi, ''); // just in case

    // Handle spaces
    matchValue = matchValue.replace(/ /gi, '\\s*');


    // Handle parents for Nucleotide mutations
    matchValue = matchValue.replace(/\(/gi, '\\(');
    matchValue = matchValue.replace(/\)/gi, '\\)');


    // Handle arrows for Nucleotide mutations
    // →
    // ->
    matchValue = matchValue.replace(/(→|->)/gi, '>');

    let labelMatchValue =  `${matchValue  }`;
    labelMatchValue = labelMatchValue.replace(/\w+:/gi, '');

    //matchValue = `(${  matchValue  })`;
    labelMatchValue = `(.*)(${ labelMatchValue })(.*)`;

    // console.log('regex', matchValue);
    // console.log('label regex', labelMatchValue);

    re = new RegExp(matchValue,"i");
    reLabel = new RegExp(labelMatchValue,"i");
  }

  //console.log(mutationsByGeneRegion);

  mutationsByGeneRegion.forEach(geneRegion => {

    let hasResults = false;

    let results;
    let count;
    let maxResultsToShow = geneRegion.resultsToShow;
    if (searchValue) {
      results = geneRegion.mutations.filter(mut => {
        return mut.mutation.name.match(re); // Search w/ no spaces
      });
      count = results.length;
      //maxResultsToShow = count;
      //maxResultsToShow = NUM_RESULTS_FOR_EACH_GENE_REGION;
    } else {
      results = geneRegion.mutations;
      count = Math.max(NUM_RESULTS_FOR_EACH_GENE_REGION, geneRegion.mutations.length);
      //maxResultsToShow = NUM_RESULTS_FOR_EACH_GENE_REGION;
    }

    //console.log(geneRegion, count);

    let mutationsHtml = `<div class="mutation-items">`;
    results.filter((mut, i) => i < maxResultsToShow).forEach(mut => {

      hasResults = true;

      //console.log(mut);

      let selectedClass = (mutations.includes(mut.mutation.name)) ? "selected" : "";
      // if (mut.mutation.refAA === 'DEL') {
      //
      //   mutationsHtml += `<div class="mutation-item ${ selectedClass }" data-mutation="${mut.mutation.name}">
      //     ${ mut.mutation.label }
      //   </div>`;
      //
      //   // mutationsHtml += `<div class="mutation-item ${ selectedClass }" data-mutation="${mut.mutation.name}">
      //   //             <span class="mutation--name--pos">∆</span>
      //   //             <span class="mutation--name--alt">${ mut.mutation.label.substring(1) }</span>
      //   //         </div>`;
      // } else {

      //let label = mut.mutation.name;

      let name = mut.mutation.name;
      let label = '';

      if (mut.mutation.refAA === 'DEL') {
        let tokens = name.split(':');
        if (tokens && tokens.length > 1) {
          label = tokens[1];
        } else {
          label = name;
        }

      } else {
        label = mut.mutation.refAA + mut.mutation.position + mut.mutation.altAA;
        if (mut.mutation.nucLabel) {
          label += ` ${  mut.mutation.nucLabel}`;
        }
      }

      let match = reLabel.exec(label);
      //console.log(name, label, match, searchValue);

      function formatMutationLabel(l) {
        l = l || '';
        return l.replace(/>/gi, '→').replace(/DEL/gi, '∆').replace(/ /gi, '&nbsp;');
      }

      if (match && match[2]) {
        label = `
          <span class="mutation--name--search">${ formatMutationLabel(match[1]) }</span>
          <span class="mutation--name--search--highlight">${ formatMutationLabel(match[2]) }</span>
          <span class="mutation--name--search">${ formatMutationLabel(match[3]) }</span>
        `;
      } else {
        label = formatMutationLabel(label);
      }

      //console.log(label);

      mutationsHtml += `<div class="mutation-item ${ selectedClass }" data-mutation="${mut.mutation.name}">
          ${ label }
        </div>`;

      // mutationsHtml += `<div class="mutation-item ${ selectedClass }" data-mutation="${mut.mutation.name}">
      //             <span class="mutation--name--ref">${ mut.mutation.refAA }</span>
      //             <span class="mutation--name--pos">${ mut.mutation.position }</span>
      //             <span class="mutation--name--alt">${ mut.mutation.altAA }</span>
      //             <span class="mutation--name--nuc">${ mut.mutation.nucLabel || '' }</span>
      //         </div>`;
      //}

    });

    matches+=count;

    let remainder = count - maxResultsToShow;

    if (remainder > 0 && remainder > ADDL_RESULTS_FOR_EACH_GENE_REGION) {
      mutationsHtml += `<div class="extra-mutations"><span data-gene-region="${ geneRegion.gene }">Show ${ ADDL_RESULTS_FOR_EACH_GENE_REGION } more (out of ${ remainder.toLocaleString() })</span></div>`;
    }

    mutationsHtml += `</div>`;

    if (hasResults) {
      resultsHtml += `<div class="mutation-region">
                  <h2 class="mutation-region-label">${ geneRegion.geneLabel }</h2>
                  ${ mutationsHtml }
                </div>`;
    }

    //geneRegion.resultsIndex = 0;

  });

  let matchesDiv = document.querySelector("#mutation-search-matches");
  let resultsDiv = document.querySelector("#mutation-search-results");

  let matchesHtml = ``;
  if (searchValue) {
    let mutationSuffix = matches === 1 ? "" : "s";
    matchesHtml = `<span>${ matches.toLocaleString() }</span> matching mutation${ mutationSuffix }`;
  }

  trackEvent('search', 'mutations search', searchValue, matches);

  matchesDiv.innerHTML = matchesHtml;
  resultsDiv.innerHTML = resultsHtml;

  if (expandedGeneRegion) {

    let extraLabel = document.querySelector(`.extra-mutations span[data-gene-region="${ expandedGeneRegion }"]`);
    extraLabel.scrollIntoView({behavior:"smooth", block:"end"});

  }

  container.querySelectorAll("[data-mutation]").forEach(mutDiv => {
    mutDiv.addEventListener("click", () => {

      //mutDiv.classList.toggle("selected", true);

      let mut = mutDiv.getAttribute("data-mutation");
      mutations.push(mut);
      document.querySelector("#mutation-search--input").value = ""; // reset
      updateMutationSelections();
      checkMaxMutations();
      closeMutationsSearch();
      loadCharts();
    });
  });

  container.querySelectorAll(".extra-mutations span").forEach(extraDiv => {
    extraDiv.addEventListener("click", () => {
      let geneRegion = extraDiv.getAttribute("data-gene-region");

      let region = mutationsByGeneRegion.find(m => m.gene === geneRegion);
      region.resultsToShow += ADDL_RESULTS_FOR_EACH_GENE_REGION;

      runMutationsSearch(searchValue, container, geneRegion);

      //console.log(region);
    });
  });
}
function mutationSearch(value) {
  openMutationsSearch(value);
}
function updateMutationSelections() {

  let container = document.querySelector("#selector--filter--controls");

  let html = ``;
  mutations.forEach((mut, i) => {
    let mutation = new Mutation(mut);

    html += `<div class="selector--filter" id="selector--filter--${ i + 1 }" data-mutation="${ mutation.name }">
      <span class="mutation--name">
        <span class="mutation--name--location">${ mutation.gene }:</span>
        `;
    if (mutation.refAA === 'DEL') {
      html += `<span class="mutation--name--pos">∆</span>
        <span class="mutation--name--alt">${ mutation.label.substring(1) }</span>`;
    } else {
      html += `<span class="mutation--name--ref">${ mutation.refAA }</span>
        <span class="mutation--name--pos">${ mutation.position }</span>
        <span class="mutation--name--alt">${ mutation.altAA }</span>
        <span class="mutation--name--nuc">${ mutation.nucLabelHtml || '' }</span>`;
    }
    html +=    `
      </span>
      <div class="filter--dismiss"><div class="box"><img class="glyph" /></div></div>
    </div>`;

  });

  container.innerHTML = html;


  // Update the URL / history with the new mutations
  // const url = new URL(window.location);
  // url.searchParams.set('mutations', mutations.join(','));
  // window.history.pushState({}, '', url);

  // Update localStorage with the new mutations
  setLocalStorage();


  // Add click events for dismissing mutations
  container.querySelectorAll(".selector--filter").forEach(mutDiv => {

    let dismiss = mutDiv.querySelector(".filter--dismiss");
    dismiss.addEventListener("click", () => {
      let mut = mutDiv.getAttribute("data-mutation");

      let mutIndex = mutations.findIndex(m => m === mut);
      mutations.splice(mutIndex, 1); // gets rid of the clicked item from the overall array

      updateMutationSelections();
      checkMaxMutations();

      if (isMutationsSearchOpen()) {
        // Remove any selected mutations that are in the search panel, if it's open
        let anySelectedMutation = document.querySelector(`#mutations .mutation-item[data-mutation="${ mut }"]`);
        if (anySelectedMutation) {
          anySelectedMutation.classList.toggle("selected", false);
        }

        if (mutations.length === 0) {
          document.querySelector("#mutations .search--dismisser").classList.toggle("hidden", true);
        } else {
          document.querySelector("#mutations .search--dismisser").classList.toggle("hidden", false);
        }
      } else if (mutations.length === 0) {
        openMutationsSearch();
      } else {
        loadCharts();
      }
    });
  });
}
function closeMutationsSearch() {
  document.querySelector("#content").classList.toggle("searching-mutations", false);
  document.querySelector("#mutation-search--input").placeholder = "Search mutations";
}
function isMutationsSearchOpen() {
  return document.querySelector("#content").classList.contains("searching-mutations");
}
function checkMaxMutations() {
  if (mutations.length >= MAX_MUTATIONS) {
    document.querySelector("#selector").classList.toggle("max-mutations", true);
  } else {
    document.querySelector("#selector").classList.toggle("max-mutations", false);
  }

  if (mutations.length > 1) {
    document.querySelector("#selector").classList.toggle("overlaps", true);
  } else {
    document.querySelector("#selector").classList.toggle("overlaps", false);
  }
}
function updateOverlappingMutations() {
  document.querySelector("#show-overlap").checked = overlappingMutationsToggle;
  if (overlappingMutationsToggle) {
    document.body.classList.toggle("overlapping", true);
  } else {
    document.body.classList.toggle("overlapping", false);
  }
}

function processMutations(mutations) {

  let minCount = 0;

  let mutationsByGeneRegion = {};
  mutations.filter(mut => mut.name && mut.count > minCount).forEach(mut => {
    mut.mutation = new Mutation(mut.name);
    mutationsByGeneRegion[mut.mutation.gene] = mutationsByGeneRegion[mut.mutation.gene] || {
      gene:mut.mutation.gene,
      geneLabel:getGeneLabel(mut.mutation.gene),
      sort:mut.mutation.geneSort,
      mutations:[]
    };
    mutationsByGeneRegion[mut.mutation.gene].mutations.push(mut);

    // Shift untranslated and non coding regions to the end of the lsit
    if (['5UTR', '3UTR', 'Non-coding region'].includes(mut.mutation.gene)) {
      mutationsByGeneRegion[mut.mutation.gene].sort += 20;
    }
  });

  let mutationsList = Object.values(mutationsByGeneRegion);
  mutationsList.sort((a, b) => a.sort - b.sort);

  return mutationsList;

}
function getGeneLabel(gene) {
  let geneLabels = {
    'S' : 'Spike'
  }
  return geneLabels[gene] || gene;
}



// Lineages

function openLineagesSearch(searchValue) {

  document.querySelector("#content").classList.toggle("searching-lineages", true);
  document.querySelector("#lineage-search--input").placeholder = "Search lineages";
  document.querySelector("#lineage-search--input").focus();

  // Hide the mutations dismisser if there are no mutations
  if (lineages.length === 0) {
    document.querySelector("#lineages .search--dismisser").classList.toggle("hidden", true);
  } else {
    document.querySelector("#lineages .search--dismisser").classList.toggle("hidden", false);
  }

  let container = document.querySelector("#lineage-search-contents");

  if (searchValue) {
    runLineagesSearch(searchValue, container);
  } else if (searchValue === "") {
    runLineagesSearch(searchValue, container);
  }



}
function runLineagesSearch(searchValue, container) {
  //if (!noData) {

  let matchesDiv = document.querySelector("#lineage-search-matches");
  let resultsDiv = document.querySelector("#lineage-search-results");

  let html = ``;
  let matches = 0;

  let re, reLabel;
  if (searchValue || searchValue === '') {
    // Handle spike to S: wildcard
    let matchValue = searchValue;

    let labelMatchValue =  `${matchValue  }`;
    labelMatchValue = labelMatchValue.replace(/\w+:/gi, '');

    //matchValue = `(${  matchValue  })`;
    labelMatchValue = `(.*)(${ labelMatchValue })(.*)`;

    // console.log('regex', matchValue);
    // console.log('label regex', labelMatchValue);

    re = new RegExp(matchValue,"i");
    reLabel = new RegExp(labelMatchValue,"i");
  }

  let hasResults = false;

  let results = allLineages;
  //let count;
  if (searchValue) {
    results = allLineages.filter(lineage => {
      return lineage.name.match(re);
    });
    matches = results.length;
  }

  let lineagesHtml = `<div class="lineage-items">`;

  results.forEach(lineage => {
    hasResults = true;

    let name = lineage.name;
    let label = name;

    lineagesHtml += `<div class="lineage-item" data-lineage="${name}">
            <span class="lineage-item--name">${ label }</span><span class="lineage-item--count">(${ lineage.count.toLocaleString() })</span>
          </div>`;
  });

  lineagesHtml += `</div>`;

  if (hasResults) {
    html += `${ lineagesHtml }`;
  }

  matchesDiv.innerHTML = "";
  resultsDiv.innerHTML = html;

  let matchesHtml = ``;
  if (searchValue) {
    let matchSuffix = matches === 1 ? "" : "s";
    matchesHtml = `${ matches } matching lineage${ matchSuffix }`;
  }
  matchesDiv.innerHTML = matchesHtml;


  trackEvent('search', 'lineages search', searchValue, matches);

  // container.innerHTML = matchesHtml + html;

  container.querySelectorAll("[data-lineage]").forEach(lineageDiv => {
    lineageDiv.addEventListener("click", () => {

      let lineage = lineageDiv.getAttribute("data-lineage");
      lineages.push(lineage);

      document.querySelector("#lineage-search--input").value = ""; // reset
      updateLineagesSelections();
      checkMaxLineages();
      closeLineagesSearch();
      loadCharts();

    });
  });

  // container.querySelectorAll(".extra-mutations span").forEach(extraDiv => {
  //   extraDiv.addEventListener("click", () => {
  //     let geneRegion = extraDiv.getAttribute("data-gene-region");
  //
  //     let region = mutationsByGeneRegion.find(m => m.gene === geneRegion);
  //     region.resultsToShow += ADDL_RESULTS_FOR_EACH_GENE_REGION;
  //
  //     runMutationsSearch(searchValue, container, geneRegion);
  //
  //     //console.log(region);
  //   });
  // });
}
function lineagesSearch(value) {
  openLineagesSearch(value);
}
function updateLineagesSelections() {

  let container = document.querySelector("#selector--filter--controls");

  let html = ``;
  lineages.forEach((lineage, i) => {

    // let mutation = new Mutation(mut);
    //
    html += `<div class="selector--filter" id="selector--filter--${ i + 1 }" data-lineage="${ lineage }">
      <span class="lineage--name">${ lineage }</span>
      <div class="filter--dismiss"><div class="box"><img class="glyph" /></div></div>
    </div>`;

  });

  container.innerHTML = html;

  // Update localStorage with the new lineages
  setLocalStorage();


  // Add click events for dismissing mutations
  container.querySelectorAll(".selector--filter").forEach(div => {

    let dismiss = div.querySelector(".filter--dismiss");
    dismiss.addEventListener("click", () => {

      let lineage = div.getAttribute("data-lineage");
      let lineageIndex = lineages.findIndex(l => l === lineage);
      lineages.splice(lineageIndex, 1);  // gets rid of the clicked item from the overall array

      updateLineagesSelections();
      checkMaxLineages();

      if (lineages.length === 0) {
        openLineagesSearch();
      } else {
        loadCharts();
      }

    });
  });
}
function closeLineagesSearch() {
  document.querySelector("#content").classList.toggle("searching-lineages", false);
  document.querySelector("#lineage-search--input").placeholder = "Search lineages";
}
function isLineagesSearchOpen() {
  return document.querySelector("#content").classList.contains("searching-lineages");
}
function checkMaxLineages() {
  if (lineages.length >= MAX_LINEAGES) {
    document.querySelector("#selector").classList.toggle("max-lineages", true);
  } else {
    document.querySelector("#selector").classList.toggle("max-lineages", false);
  }

  if (lineages.length > 1) {
    document.querySelector("#selector").classList.toggle("overlaps", true);
  } else {
    document.querySelector("#selector").classList.toggle("overlaps", false);
  }
}

// Countries
// =======================
function openCountriesSearch(searchValue) {
  document.querySelector("#content").classList.toggle("searching-countries", true);

  let container = document.querySelector("#countries");
  container.innerHTML = "";

  let html = ``;

  // Handle searching
  let re;
  if (searchValue) {

    re = new RegExp(searchValue,"gi");

    let countriesHtml = ``;
    let matches = 0;
    let provinceMatches = 0;

    let countryClass = "";
    let provinceClass = "";

    // Search matching countries

    allCountries.forEach(country => {

      let details = getCountryDetails(country.name);

      // Searching using the GISAID name, the mapped displayName, and any synonyms
      let searches = [country.name, details.displayName, ...details.synonyms];
      let searchMatches = searches.find(s => {
        return s.match(re);
      });

      if (searchMatches) {
        let id = getCountryId(country.name);
        //let name = country.name;

        let selectedClass = (countries.includes(country.name)) ? "selected" : "";

        countriesHtml += `<div class="country-item ${ selectedClass }" data-country="${ country.name }" id="${ id }">
                  <div class="country--flag"><img /></div>
                  <div class="country--name">${ details.displayName }</div>
                </div>`;

        matches++;
      }

    });

    if (matches === 0) {
      countryClass = " empty";
    }

    // Search any matching provinces/states
    let provincesHtml = ``;

    for (const country in provincesByCountry) {

      let provinces = [...provincesByCountry[country].provinces, ...provincesByCountry[country].extras];

      provinces.forEach(province => {

        let details = getProvinceDetails(province);

        // Searching using the GISAID name, the mapped displayName, and any synonyms
        let searches = [province, details.displayName, ...details.synonyms];
        let searchMatches = searches.find(s => {
          return s.match(re);
        });

        if (searchMatches) {
          let id = `${ getCountryId(country) }${ PROVINCE_SEPARATOR }${ getProvinceId(province) }`;

          let selectedClass = (countries.includes(id)) ? "selected" : "";

          provincesHtml += `<div class="province-item" data-country="${ country }" data-province="${ province }" id="${ id }"><div class="province--shape"><img /></div><div class="province--name">${ details.displayName }</div></div>`;

          matches++;
          provinceMatches++;
        }

      });

    }

    if (provinceMatches === 0) {
      provinceClass = " empty";
    }

    let suffix = (matches === 1) ? "" : "es";

    html += `<div id="country-search-matches" class="search-matches"><span>${ matches.toLocaleString() }</span> match${ suffix }</div>`;

    html += `<div class="country-items${ countryClass }">${ countriesHtml }</div>`;
    html += `<div class="province-items${ provinceClass }">${ provincesHtml }</div>`;





  // Handle selecting
  } else {

    for (const region in countriesByRegion) {

      let hasSubRegions = false;
      let subRegionsHtml = ``;

      for (const subRegion in countriesByRegion[region]) {

        let hasCountries = false;
        let countriesHtml = ``;

        countriesByRegion[region][subRegion].forEach(country => {

          hasCountries = true;
          hasSubRegions = true;

          let id = getCountryId(country.name);
          let name = country.name;
          let details = getCountryDetails(name);

          let selectedClass = (countries.includes(country.name)) ? "selected" : "";

          countriesHtml += `<div class="country-item ${ selectedClass }" data-country="${ country.name }" id="${ id }">
                  <div class="country--flag"><img /></div>
                  <div class="country--name">${ details.displayName }</div>
                </div>`;

        });

        if (hasCountries) {
          subRegionsHtml += `<div class="continent-region">
            <h3 class="continent-region-label">${ subRegion }</h3>
            <div class="country-items">
              ${ countriesHtml }
            </div>
          </div>`;

          // console.log(statesByCountry);
          countriesByRegion[region][subRegion].forEach(country => {

            // console.log(getCountryId(country.name));
            // console.log(statesByCountry[getCountryId(country.name)]);
            if (provincesByCountry[country.name]) {

              let provinceGroups = provincesByCountry[country.name];

              let provinceGroupsHtml = ``;


              for (const group in provinceGroups) {

                let provinceItemHtml = ``;

                provinceGroups[group].forEach(province => {

                  let details = getProvinceDetails(province);

                  provinceItemHtml += `<div class="province-item" data-country="${ country.name }" data-province="${ province }" id="${ getCountryId(country.name) }${ PROVINCE_SEPARATOR }${ getProvinceId(province) }"><div class="province--shape"><img /></div><div class="province--name">${ details.displayName }</div></div>`;
                });

                provinceGroupsHtml += `
                    <div class="province-items">
                      ${ provinceItemHtml }
                    </div>
                `;

              }



              subRegionsHtml += `<div class="continent-region">
                  <h3 class="continent-region-label">${ country.name } States</h3>
                  <div class="province-item-group">
                    ${ provinceGroupsHtml }
                  </div>
                </div>`;

            }
          });

        }

      }


      if (hasSubRegions) {
        html += `<div class="continent">
                  <h2 class="continent-label">${ region }</h2>
                  <div class="continent-regions">
                    ${ subRegionsHtml }
                  </div>
                </div>`;
      }

    }

  }

  container.innerHTML = html;

  container.querySelectorAll("[data-country]:not([data-province])").forEach(countryDiv => {
    countryDiv.addEventListener("click", () => {
      //countryDiv.classList.toggle("selected", true);

      let country = countryDiv.getAttribute("data-country");
      countries.push(country);

      closeCountriesSearch();
      loadCharts();

      document.querySelector("#country-search--input").placeholder = "Add a country or state";

      checkMaxCountries();

      // Update the URL / history with the new countries
      // const url = new URL(window.location);
      // url.searchParams.set('countries', countries.join(','));
      // window.history.pushState({}, '', url);

      // Update localStorage with the new countries
      setLocalStorage();


    });
  });

  container.querySelectorAll("[data-province]").forEach(provinceDiv => {
    provinceDiv.addEventListener("click", () => {

      let country = provinceDiv.getAttribute("data-country");
      let province = provinceDiv.getAttribute("data-province");


      countries.push(`${country  }${ PROVINCE_SEPARATOR }${  province}`);

      closeCountriesSearch();
      loadCharts();

      document.querySelector("#country-search--input").placeholder = "Add a country or state";

      checkMaxCountries();

      // Update localStorage with the new province
      setLocalStorage();


    });
  });
  //});
}
function countrySearch(value) {
  openCountriesSearch(value);
}
function isCountriesSearchOpen() {
  return document.querySelector("#content").classList.contains("searching-countries");
}
function closeCountriesSearch() {
  document.querySelector("#content").classList.toggle("searching-countries", false);
  document.querySelector("#country-search--input").value = "";
}
function checkMaxCountries() {
  if (countries.length >= MAX_COUNTRIES) {
    document.querySelector("#geographer").classList.toggle("max", true);
  } else {
    document.querySelector("#geographer").classList.toggle("max", false);
  }
}

function processCountries(allCountries, countriesByRegion) {

  for (const region in countriesByRegion) {
    for (const subRegion in countriesByRegion[region]) {

      let mappedCountries = [];

      countriesByRegion[region][subRegion].forEach(countryName => {

        let country = allCountries.find(c => c.name === countryName);
        if (country) {
          mappedCountries.push(country);
        } else {
          //console.log(`could not find ${  countryName}`);
        }
      });

      countriesByRegion[region][subRegion] = mappedCountries;
    }

  }
}






// Charts

function loadCharts() {

  showLoader();

  if (dataMode === "mutations") {
    loadMutationsData(mutations, countries, overlappingMutationsToggle,
      (data, _mostRecentDataDate) => {
        mostRecentDataDate = _mostRecentDataDate;

        setDataCopy(data);
        data = filterData(data);
        draw(data);
      },
      (err) => {
        handleError(err);
      }
    );
  } else if (dataMode === "lineages") {

    loadLineagesData(lineages, countries,
      (data, _mostRecentDataDate) => {
        mostRecentDataDate = _mostRecentDataDate;

        setDataCopy(data);
        data = filterData(data);
        draw(data);
      },
      (err) => {
        handleError(err);
      }
    );

  }


}

// Simple shallow clones to make timeframe switching easier
function setDataCopy(d) {

  let copy = [];
  d.forEach(c => {
    let o = {...c};
    o.data = [...o.data];
    copy.push(o);
  });

  processedData = copy;

}
function getDataCopy() {

  if (processedData) {

    let copy = [];
    //console.log('getDataCopy', processedData);
    processedData.forEach(c => {
      let o = {...c};
      o.data = [...o.data];
      copy.push(o);
    });

    return copy;

  } else {
    return null;
  }

}

function getTimeframe() {
  return timeframe;
}
function setTimeframeFromUI() {
  let timeframeEl = document.querySelector("#selector--date--menu--options li[data-timeframe].active");
  if (timeframeEl) {
    setTimeframe(timeframeEl.getAttribute("data-timeframe"));
  } else {
    // custom?
    let customDateEl = document.querySelector("#selector--date--menu--options li.custom.active");
    if (customDateEl) {
      let dateStartValue = document.querySelector("#selector--date--custom--date-start").value;
      let dateEndValue = document.querySelector("#selector--date--custom--date-end").value;
      setCustomDateTimeframe(dateStartValue, dateEndValue);
    }
  }
}
function setTimeframe(tf) {
  timeframe = tf;
  setLocalStorage(); // update localStorage with the timeframe
}
function setCustomDateTimeframe(startTf, endTf) {
  timeframe = {start:startTf, end:endTf};
  setLocalStorage(); // update localStorage with the timeframe

}

// Timeframe updates
function updateTimeframeSelection() {
  let timeframe = getTimeframe();

  let timeframeLabel;

  // Make sure the global startDate and endDate are set
  let data = getDataCopy();
  data = filterData(data);

  let li = document.querySelector(`#selector--date--menu--options li[data-timeframe="${ timeframe }"]`);
  if (li) {
    timeframeLabel = li.innerHTML;

    //dateEnd = mostRecentDataDate;
    //dateStart = dateEnd.minus({days:timeframe})

  } else { //custom
    //dateStart = DateTime.fromISO(timeframe.start);
    //dateEnd   = DateTime.fromISO(timeframe.end);

    li = document.querySelector(`#selector--date--menu--options li.custom`);
    timeframeLabel = `${ startDate.toFormat(CHART_DATE_FORMAT) } to ${ endDate.toFormat(CHART_DATE_FORMAT) }`;
  }

  // Update the custom dates with current timeframe
  document.querySelector("#selector--date--custom--date-start").value = `${ startDate.toFormat(DATE_FORMAT_CUSTOM_VALUE) }`;
  document.querySelector("#selector--date--custom--date-end").value = `${ endDate.toFormat(DATE_FORMAT_CUSTOM_VALUE) }`;

  // Disable all of them
  document.querySelectorAll("#selector--date--menu--options li").forEach(_li => _li.classList.toggle("active", false));
  li.classList.toggle("active", true);

  // Set the label
  document.querySelector("#selector--date--selection").innerHTML = `${ timeframeLabel }`;

}

function getFrequency() {
  return frequency;
}
function setFrequency(freq) {
  frequency = freq;
}

function filterData(data) {

  let filteredData;

  if (mostRecentDataDate) {

    endDate = mostRecentDataDate;
    startDate = mostRecentDataDate;

    //console.log('startDate', startDate);

    let tf = getTimeframe();
    let freq = "daily";

    if (tf === "2weeks") {
      startDate = startDate.minus({'weeks':2});
    } else if (tf === "4weeks") {
      startDate = startDate.minus({'weeks':4});
    } else if (tf === "4months") {
      startDate = startDate.minus({'months':4});
    } else if (tf === "1year") {
      startDate = startDate.minus({'year':1});
      freq = "weekly";
    } else if (tf === "all") {
      freq = "weekly";
    } else { // custom date
      startDate = DateTime.fromISO(tf.start);
      endDate = DateTime.fromISO(tf.end);
      let diff = endDate.diff(startDate, 'days');
      let days = diff.days;
      if (days > 120) {
        freq = "weekly";
      }
    }

    //console.log(startDate.toISODate(), endDate.toISODate());

    setFrequency(freq);

    filteredData = data.map(loc => {

      if (tf !== "all") {
        loc.data = loc.data.filter(d => d.date >= startDate && d.date <= endDate);
        loc.weeklyData = loc.weeklyData.filter(d => d.date >= startDate && d.date <= endDate);
      }

      let sequencesFiltered = 0;
      loc.data.forEach(d => sequencesFiltered += d.sequencesCount);
      loc.sequencesFiltered = sequencesFiltered;

      //loc.maxPct = d3.max([...loc.data, ...loc.weeklyData], d => d.pct);

      return loc;

    });

  } else {
    filteredData = data;
  }

  return filteredData;

}

function draw(data) {

  hideLoader();

  updateTimeframeSelection();

  //setMostRecentDate(mostRecentDataDate);

  // Clear out old cards
  let container = document.querySelector("#charter");
  container.querySelectorAll(".card:not(.blank)").forEach(node => {
    container.removeChild(node);
  });
  container.querySelector(".card.blank").classList.toggle("hidden", true);

  let groupedByLocation = groupBy(data, 'location');

  for (const loc in groupedByLocation) {
    let loc_id = groupedByLocation[loc][0].location_id;

    // Get the card templates ready -- all need to be in the dom before we can draw
    let template = document.querySelector(".card.blank");

    let card = template.cloneNode(true);
    card.id = loc_id;
    card.classList.remove("blank");
    card.classList.toggle("hidden", false);

    container.insertBefore(card, template);

  }

  Events.reset('dateHighlight');

  let maxPct = d3.max(data, d => {
    if (getFrequency() === "weekly") {
      return d3.max(d.weeklyData, dp => dp.pct);
    } else {
      return d3.max(d.data, dp => dp.pct);
    }
  });

  let min, max;
  let allDatesKeyed = {};

  if (getFrequency() === "weekly") {
    data.forEach(m => {
      let extents = d3.extent(m.weeklyData, d => d.date);
      min = d3.min([extents[0], min]);
      max = d3.max([extents[1], max]);

      m.weeklyData.forEach(d => {
        if (d.pct !== undefined) {
          allDatesKeyed[d.date.toISODate()] = true;
        }
      });
    });
  } else {
    data.forEach(m => {
      let extents = d3.extent(m.data, d => d.date);
      min = d3.min([extents[0], min]);
      max = d3.max([extents[1], max]);

      m.data.forEach(d => {
        if (d.pct !== undefined) {
          allDatesKeyed[d.date.toISODate()] = true;
        }
      });
    });
  }

  let allDates = Object.keys(allDatesKeyed);

  for (const loc in groupedByLocation) {
    let loc_id = groupedByLocation[loc][0].location_id;
    let isProvince = groupedByLocation[loc][0].isProvince;
    drawChart(groupedByLocation[loc], loc, loc_id, allDates, isProvince, maxPct, min, max);
  }

}

function resize(data) {
  let groupedByLocation = groupBy(data, 'location');

  Events.reset('dateHighlight');

  let maxPct = d3.max(data, d => {
    if (getFrequency() === "weekly") {
      return d3.max(d.weeklyData, dp => dp.pct);
    } else {
      return d3.max(d.data, dp => dp.pct);
    }
  });

  let min, max;
  let allDatesKeyed = {};
  if (getFrequency() === "weekly") {
    data.forEach(m => {
      let extents = d3.extent(m.weeklyData, d => d.date);
      min = d3.min([extents[0], min]);
      max = d3.max([extents[1], max]);

      m.weeklyData.forEach(d => {
        if (d.pct !== undefined) {
          allDatesKeyed[d.date.toISODate()] = true;
        }
      });
    });
  } else {
    data.forEach(m => {
      let extents = d3.extent(m.data, d => d.date);
      min = d3.min([extents[0], min]);
      max = d3.max([extents[1], max]);

      m.data.forEach(d => {
        if (d.pct !== undefined) {
          allDatesKeyed[d.date.toISODate()] = true;
        }
      });
    });
  }

  let allDates = Object.keys(allDatesKeyed);

  for (const loc in groupedByLocation) {
    let loc_id = groupedByLocation[loc][0].location_id;
    let isProvince = groupedByLocation[loc][0].isProvince;
    drawChart(groupedByLocation[loc], loc, loc_id, allDates, isProvince, maxPct, min, max);
  }
}

function drawChart(data, locationLabel, locationId, allDates, isProvince, maxPct, min, max) {

  //maxPct = maxPct || 1;

  let card = document.querySelector(`#charter #${ locationId }`);

  // Draw the actual chart

  let margin = {top:5, right:15, bottom:5, left:36};

  let svgWrapper = card.querySelector('.card--chart');
  svgWrapper.innerHTML = "";

  const svg = d3.create("svg")
    .attr("width", "100%")
    .attr("height", "100%")
    .attr("xmlns", "http://www.w3.org/2000/svg");

  svgWrapper.appendChild(svg.node());

  // This method of getting width/height for axes respects display:flex and 100% width/height on the layout
  let bb = svg.node().getBoundingClientRect();
  let width = bb.width;
  let height = bb.height;

  x = d3.scaleUtc();
  x.domain([min, max]);
  x.range([margin.left, width - margin.right]);

  y = d3.scaleLinear()
    .domain([0, maxPct])
    .range([margin.bottom, height - margin.top])
    .nice();

  let yDomain = y.domain(); // to get any 'nice'd values

  let ticks = getTickIntervals(x);
  let tf = getTimeframe();

  // custom stack order to optimize for color heirarchy and contrast
  let svgStackOrder = [
    //0, 2, 1
    2, 0, 1
  ];
  data.forEach((mutData, i) => {
    mutData.index = i;
    mutData.svgStackOrder = svgStackOrder[i];
  });

  data.sort((a, b) => a.svgStackOrder - b.svgStackOrder);

  data.forEach(mutData => {

    //console.log(i);


    let chartData = (getFrequency() === "weekly") ? mutData.weeklyData : mutData.data;
    let uncertains = [];
    let certainEnoughs = [];
    let writingToUncertains = true;
    let current = [];
    let previous = null;

    chartData.filter(d => d.pct !== undefined).forEach((dataPoint, i)=>{
      if (dataPoint.denom < UNCERTAIN_THRESHOLD) {
        if (i === 0 || !writingToUncertains) {
          current = [];
          if (previous !== null) {
            current.push(previous);
          }
          uncertains.push(current);
        }
        writingToUncertains = true;
      } else {
        if (writingToUncertains) {
          if (i > 0) {
            current.push(dataPoint);
          }
          current = [];
          certainEnoughs.push(current);
        }
        writingToUncertains = false;
      }
      current.push(dataPoint);
      previous = dataPoint;
    });


    const line = d3.line()
      .x(d => x(d.date))
      .y(d => y(yDomain[1]) - y(d.pct) + margin.top);

    const area = d3.area()
      .x(d => x(d.date))
      .y1(d => y(yDomain[1]) - y(d.pct) + margin.top)
      .y0(y(yDomain[1]));

    let g = svg.append("g")
      .attr("class", "chartLayer");

    // Area
    // =======================

    // g.append("path")
    //   .attr("class", `chartArea chartArea-${ mutData.index + 1}`)
    //   //.attr("fill", color)
    //   .attr("d", area(chartData));


    certainEnoughs.forEach(pathData=>{
      g.append("path")
        .attr("class", `chartArea chartArea-${ mutData.index + 1}`)
        //.attr("fill", color)
        .attr("d", area(pathData.filter(d => d.pct !== undefined)));
    });
    // Gridlines
    // =======================

    let drawGrid = mutData.svgStackOrder === 0;
    if (drawGrid) {

      let grid = g.append("g")
        .attr("class", "chart--gridlines");

      ticks.forEach(tick => {
        grid.append("line")
          .attr("class", "chart--gridline")
          .attr("data-date", DateTime.fromJSDate(tick).toISODate())
          .attr("x1", Math.round(x(tick)))
          .attr("y", y(yDomain[0]))
          .attr("x2", Math.round(x(tick)))
          .attr("y2", y(yDomain[1]));
      });
    }

    // Line
    // =======================

    certainEnoughs.forEach(pathData=>{
      g.append("path")
        .attr("class", `chartLine chartLine-${ mutData.index + 1 }`)
      //.attr("stroke", color)
        .attr("d", line(pathData.filter(d => d.pct !== undefined)));
    });

    uncertains.forEach(pathData=>{
      //console.log(pathData);
      g.append("path")
        .attr("class", `chartLine uncertain chartLine-${ mutData.index + 1 }`)
        //.attr("stroke", color)
        .attr("d", line(pathData.filter(d => d.pct !== undefined)));
    });


  });


  svgOffset = getOffset(document.querySelector("svg"));
  let charterOffset = getOffset(document.querySelector("#charter"));

  // Y Axis Left Labels

  let yLabelsHtml = ``;

  if (!isNaN(yDomain[0]) && !isNaN(yDomain[1])) {

    yLabelsHtml += `<div class="value" style="top:${ Math.round(y(yDomain[1])) }px">0%</div>`;
    yLabelsHtml += `<div class="value" style="top:${ Math.round(y(yDomain[0])) }px">${ pct(yDomain[1], 1, true, true) }</div>`;

  }

  let yLabelsDiv = card.querySelector(".card--axis--value");
  //yLabelsDiv.style.top = `${ y(maxPct)}px`;
  yLabelsDiv.style.top = `${ svgOffset.top - charterOffset.top - 1 }px`;
  yLabelsDiv.innerHTML = yLabelsHtml;


  // X Axis Bottom labels
  // =======================

  let labelsHtml = ``;
  ticks.forEach((tick, i) => {

    let date = DateTime.fromJSDate(tick);
    let label = getIntervalLabel(date, tf, i===0);

    labelsHtml += `<div class="interval" data-date="${ date.toISODate() }" style="left:${ Math.round(x(tick)) }px">${ label }</div>`;

  });


  // Add a floating highlight/readout label
  labelsHtml += `<div class="interval readout" style="left:0px"></div>`;

  let labelsDiv = card.querySelector(".card--axis");
  labelsDiv.innerHTML = labelsHtml;

  let readoutDateLabel = labelsDiv.querySelector('.readout');

  const DOT_RADIUS = 4;

  let highlight = svg.append("g");
  highlight.attr("class", "chart-highlight");

  let highlightDots = [];
  data.forEach(mutData => {
    highlightDots[mutData.index] =
      highlight.append("circle")
        .attr("class", `chart-highlight-dot chart-highlight-dot${ mutData.index + 1 }`)
        .attr("cx", -100)
        .attr("cy", 0)
        .attr("r", DOT_RADIUS);
  });

  // Now, undo the svgStackOrder sorting
  data.sort((a, b) => a.index - b.index);


  let readoutLabelsDiv = card.querySelector(".readout--labels");
  let readoutLabelsWidth = readoutLabelsDiv.offsetWidth;
  let readoutChartWidth = card.querySelector(".card--chart").offsetWidth;
  let readoutLabelsMutations = readoutLabelsDiv.querySelectorAll(".readout--mutation .readout--label");
  readoutLabelsMutations.forEach(label => {
    label.querySelector(".mutation--value").innerHTML = "";
    label.querySelector(".mutation--name--ref").innerHTML = "";
    label.querySelector(".mutation--name--pos").innerHTML = "";
    label.querySelector(".mutation--name--alt").innerHTML = "";
    label.querySelector(".mutation--name--nuc").innerHTML = "";
    label.querySelector(".mutation--tally").innerHTML = "";
  });

  let readoutLabelsLineages = readoutLabelsDiv.querySelectorAll(".readout--lineage .readout--label");
  readoutLabelsLineages.forEach(label => {
    label.querySelector(".lineage--value").innerHTML = "";
    label.querySelector(".lineage--source").innerHTML = "";
    label.querySelector(".lineage--tally").innerHTML = "";
  });

  let overlapReadoutDiv = card.querySelector(".overlap--readout--labels");
  let overlapReadoutWidth = overlapReadoutDiv.offsetWidth;
  let overlapChartWidth = card.querySelector(".card--chart").offsetWidth;
  let overlapReadoutFiltersDiv = overlapReadoutDiv.querySelector(".overlap--readout--filters");
  let overlapReadoutFilters = overlapReadoutFiltersDiv.querySelectorAll(".overlap--readout--filter");
  let counter = 0;
  overlapReadoutFilters.forEach(filterLabel => {
    filterLabel.innerHTML = "";
    counter++;
  });
  let overlapReadoutValueElement = overlapReadoutDiv.querySelector(".overlap--readout--value span");
  overlapReadoutValueElement.innerHTML = "";
  let overlapReadoutCountElement = overlapReadoutDiv.querySelector(".overlap--readout--count span");
  overlapReadoutCountElement.innerHTML = "";

  let highlightLine = card.querySelector(".card--readout--line");

  // Run the highlight/readout routine

  Events.on('dateHighlight', x => showHighlight(x));

  let currentHighlightDate_chart;

  function showHighlight(inputXCoord) {

    let fudgeFactor = 5;

    let date = DateTime.fromJSDate(x.invert(inputXCoord + fudgeFactor));
    date = date.set({hour:0, minute:0});

    // If we're weekly, interpolate to the nearest week start
    if (getFrequency() === "weekly") {
      let weekDate = d3.timeWeek(DateTime.fromISO(date));
      date = DateTime.fromJSDate(new Date(weekDate));
    }

    if (date.toISODate() === currentHighlightDate_chart) {
      return;
    }
    currentHighlightDate_chart = date.toISODate();

    if (!allDates.find(d => d === date.toISODate())) {
      return;
    }

    if (!card) return;


    currentHighlightDate = date;

    //let xCoord = Math.round(x(dataPt.date));
    let xCoord = Math.round(x(date));

    // Position the highlight line on the chart
    highlightLine.style.left = `${ xCoord }px`;


    // Hide any gridlines the highlightLine may be over
    let gridlines = card.querySelectorAll(`.chart--gridlines .chart--gridline`);
    gridlines.forEach(gridline => {
      gridline.classList.toggle("hide", false);
    });
    let gridline = card.querySelector(`.chart--gridlines [data-date="${ date.toISODate() }"]`);
    if (gridline) {
      gridline.classList.toggle("hide", true);
    }

    // Set the date and position on the readout label
    let label = getIntervalLabel(date, tf, false, true);
    readoutDateLabel.style.left = `${ xCoord }px`;
    readoutDateLabel.innerHTML = `${ label }`;

    // Get ready for the rest of the readout
    let highlights = [];

    data.forEach((itemData, i) => {

      let dataPt;

      if (getFrequency() === "daily") {
        dataPt = itemData.data.find(d => d.date.toISODate() === date.toISODate());
      } else if (getFrequency() === "weekly") {
        dataPt = itemData.weeklyData.find(d => d.date.toISODate() === date.toISODate());
      }

      let yCoord, label, value, count, uncertain;
      let label_gene, label_ref, label_pos, label_alt, label_nuc;

      let readoutLabel, overlapReadoutFilterLabel;

      if (dataPt && dataPt.pct) {

        yCoord = y(yDomain[1]) - y(dataPt.pct);
        label = pct(dataPt.pct, 1, false, true);
        value = dataPt.pct;
        count = dataPt.num;
        uncertain = dataPt.denom < UNCERTAIN_THRESHOLD;
        readoutLabelsDiv.classList.toggle('uncertain', uncertain);
        overlapReadoutDiv.classList.toggle('uncertain', uncertain);
      } else {

        yCoord = -1000;
        label = '<span class="dash">&mdash;</span>';
        value = null;
        count = 0;


      }




      if (dataMode === 'mutations') {

        readoutLabel = readoutLabelsMutations[i];
        overlapReadoutFilterLabel = overlapReadoutFilters[i];

        readoutLabel.querySelector(".mutation--value").innerHTML = label;
        overlapReadoutValueElement.innerHTML = label;


        if (itemData.mutation.refAA === 'DEL') {

          //readoutLabel.querySelector(".mutation--name--ref").innerHTML = label_ref;
          readoutLabel.querySelector(".mutation--name--pos").innerHTML = '∆';
          readoutLabel.querySelector(".mutation--name--alt").innerHTML = itemData.mutation.label.substring(1);
          //readoutLabel.querySelector(".mutation--name--nuc").innerHTML = label_nuc;

          overlapReadoutFilterLabel.innerHTML = `∆${ itemData.mutation.label.substring(1)}`;

        } else {

          label_ref = itemData.mutation.refAA;
          label_pos = itemData.mutation.position;
          label_alt = itemData.mutation.altAA;
          label_nuc = itemData.mutation.nucLabelHtml;

          readoutLabel.querySelector(".mutation--name--ref").innerHTML = label_ref;
          readoutLabel.querySelector(".mutation--name--pos").innerHTML = label_pos;
          readoutLabel.querySelector(".mutation--name--alt").innerHTML = label_alt;
          readoutLabel.querySelector(".mutation--name--nuc").innerHTML = label_nuc;

          label_gene = itemData.mutation.gene;

          overlapReadoutFilterLabel.innerHTML = `${label_gene  }:${  label_ref  }${label_pos  }${label_alt  }${label_nuc}`;
          if (i < (data.length - 2)) {
            overlapReadoutFilterLabel.innerHTML += ", ";
          } else if (i === (data.length - 2)){
            overlapReadoutFilterLabel.innerHTML += " and ";
          }

        }

        let genomeCountPlural = 's';
        if (count === 1) {
          genomeCountPlural = '';
        }

        // put the count here
        let mutTally = count;
        let mutTallyReadout = mutTally.toLocaleString('en-US');
        if (mutTally === 0) {
          mutTallyReadout = "—";
          readoutLabel.querySelector(".mutation--tally").classList.toggle("dash", true);
        } else {
          readoutLabel.querySelector(".mutation--tally").classList.toggle("dash", false);
        }
        readoutLabel.querySelector(".mutation--tally").innerHTML = mutTallyReadout;
        overlapReadoutCountElement.innerHTML = `${count.toLocaleString('en-US')} genome${ genomeCountPlural}`;

      } else if (dataMode === 'lineages') {

        readoutLabel = readoutLabelsLineages[i];

        readoutLabel.querySelector(".lineage--value").innerHTML = label;
        readoutLabel.querySelector(".lineage--source").innerHTML = itemData.lineage;

        let linTally = count;
        let linTallyReadout = linTally.toLocaleString('en-US');
        if (linTally === 0) {
          linTallyReadout = "—";
          readoutLabel.querySelector(".lineage--tally").classList.toggle("dash", true);
        } else {
          readoutLabel.querySelector(".lineage--tally").classList.toggle("dash", false);
        }
        readoutLabel.querySelector(".lineage--tally").innerHTML = linTallyReadout;

      }



      highlights.push({
        index : i,
        xCoord : xCoord,
        yCoord : yCoord,
        label  : readoutLabel,
        pct    : value
      });

    });

    // Reset any unused readout labels
    readoutLabelsMutations.forEach((label, i) => {
      if (i >= highlights.length) {
        label.querySelector(".mutation--value").innerHTML = "";
        // label.querySelector(".mutation--name--location").innerHTML = "";
        label.querySelector(".mutation--name--ref").innerHTML = "";
        label.querySelector(".mutation--name--pos").innerHTML = "";
        label.querySelector(".mutation--name--alt").innerHTML = "";
        label.querySelector(".mutation--name--nuc").innerHTML = "";
        label.querySelector(".mutation--tally").innerHTML = "";
      }
      label.classList.toggle("single-1", false);
      label.classList.toggle("duo-1", false);
      label.classList.toggle("duo-2", false);
      label.classList.toggle("trio-1", false);
      label.classList.toggle("trio-2", false);
      label.classList.toggle("trio-3", false);
    });

    readoutLabelsLineages.forEach((label, i) => {
      if (i >= highlights.length) {
        label.querySelector(".lineage--value").innerHTML = "";
        label.querySelector(".lineage--source").innerHTML = "";
        label.querySelector(".lineage--tally").innerHTML = "";
      }
      label.classList.toggle("single-1", false);
      label.classList.toggle("duo-1", false);
      label.classList.toggle("duo-2", false);
      label.classList.toggle("trio-1", false);
      label.classList.toggle("trio-2", false);
      label.classList.toggle("trio-3", false);
    });


    if (highlights.length > 0) {

      let highlightClassName = `single-`;
      if (highlights.length === 2) {
        highlightClassName = 'duo-';
      } else if (highlights.length === 3) {
        highlightClassName = 'trio-';
      }

      // Sort the highlights by their value to create a leaderboard class system
      highlights.sort((a, b) => b.pct - a.pct);

      highlights.forEach((highlight, i) => {

        highlightDots[highlight.index].attr("cx", highlight.xCoord);
        highlightDots[highlight.index].attr("cy", highlight.yCoord + DOT_RADIUS);
        highlightDots[highlight.index].attr("r", DOT_RADIUS);


        highlight.label.classList.toggle(`${ highlightClassName }${ i + 1 }`, true);

      });

      let readoutLabelsSourceWidth, readoutLabelsValueWidth;

      //readoutLabelsWidth = readoutLabelsDiv.offsetWidth;
      if (dataMode === "mutations") {
        readoutLabelsWidth = readoutLabelsMutations[0].getBoundingClientRect().width;
        readoutLabelsSourceWidth = readoutLabelsMutations[0].querySelector(".mutation--source").getBoundingClientRect().width;
        readoutLabelsValueWidth = readoutLabelsMutations[0].querySelector(".mutation--value").getBoundingClientRect().width;
      } else if (dataMode === "lineages") {
        readoutLabelsWidth = readoutLabelsLineages[0].getBoundingClientRect().width;
        readoutLabelsSourceWidth = readoutLabelsLineages[0].querySelector(".lineage--source").getBoundingClientRect().width;
        readoutLabelsValueWidth = readoutLabelsLineages[0].querySelector(".lineage--value").getBoundingClientRect().width;
      }

      let readoutLabelXCoord = xCoord - (readoutLabelsWidth / 2);

      if (xCoord < (readoutLabelsWidth)) {
        readoutLabelsDiv.classList.add('align-left');
        readoutLabelXCoord = xCoord;
      } else {
        readoutLabelsDiv.classList.remove('align-left');
      }
      if (xCoord > readoutChartWidth - (readoutLabelsWidth)) {
        readoutLabelsDiv.classList.add('align-right');
        readoutLabelXCoord = xCoord - readoutLabelsWidth;
      } else {
        readoutLabelsDiv.classList.remove('align-right');
      }
      if ((xCoord >= (readoutLabelsWidth)) && (xCoord <= readoutChartWidth - (readoutLabelsWidth))) {
        readoutLabelXCoord = xCoord - readoutLabelsSourceWidth - (readoutLabelsValueWidth * 0.5);
      }

      readoutLabelsDiv.style.left = `${ readoutLabelXCoord }px`;

      if (xCoord < (overlapReadoutWidth)) {
        overlapReadoutDiv.classList.add('align-left');
        readoutLabelXCoord = xCoord;
      } else {
        overlapReadoutDiv.classList.remove('align-left');
      }
      if (xCoord > overlapChartWidth - (overlapReadoutWidth)) {
        overlapReadoutDiv.classList.add('align-right');
        readoutLabelXCoord = xCoord;
      } else {
        overlapReadoutDiv.classList.remove('align-right');
      }

      overlapReadoutDiv.style.left = `${ readoutLabelXCoord }px`;

    }
  }


  card.querySelector(".country--dismiss").addEventListener("click", dismissCountry);
  card.querySelector(".country--dismiss").addEventListener("touchstart", dismissCountry, {passive:true});

  function dismissCountry() {
    let country = locationId;
    let countryIndex = countries.findIndex(c => c.toLowerCase() === country.toLowerCase());

    countries.splice(countryIndex, 1); // gets rid of the clicked item from the overall array

    loadCharts();
    checkMaxCountries();


    // Update localStorage with the countries
    setLocalStorage();

    if (countries.length === 0) {
      document.querySelector("#country-search--input").placeholder = "Filter to a country";
    } else if (countries.length === 1) {
      document.querySelector("#country-search--input").placeholder = "Add a country or state";
    }
  }

  let countryName = card.querySelector(".country--name");

  let details;

  card.classList.toggle("card--province", false);
  card.classList.toggle("card--country", false);

  if (isProvince) {
    details = getProvinceDetails(locationLabel);
    card.classList.toggle("card--province", true);
  } else {
    details = getCountryDetails(locationLabel);
    card.classList.toggle("card--country", true);
  }

  countryName.innerHTML = details.displayName;

  // Country match sequences

  let sequencesTotal = d3.max(data, d => d.sequencesTotal);
  let sequencesFiltered = d3.max(data, d => d.sequencesFiltered);

  let sequencesInRange = 0;
  if (sequencesTotal > 0) {
    sequencesInRange = sequencesFiltered / sequencesTotal;
  }

  let countryMatchCountValue = card.querySelector(".country--match--count--value");
  countryMatchCountValue.innerHTML = `${ sequencesTotal.toLocaleString() }`;

  let countryMatchCountText = card.querySelector(".country--match--count--text");
  let countryMatchCountTextPlural = 's';

  if (sequencesTotal === 1) {
    countryMatchCountTextPlural = '';
  }

  let countryMatchLabel = `genome${ countryMatchCountTextPlural} filtered`;

  countryMatchCountText.innerHTML = `${ countryMatchLabel }`;

  // Country match power bars

  let countryMatchBars = card.querySelector(".country--match--count--bars");
  countryMatchBars.classList.toggle("scale-1", sequencesTotal < 100);
  countryMatchBars.classList.toggle("scale-2", sequencesTotal >= 100 && sequencesTotal <= 1000);
  countryMatchBars.classList.toggle("scale-3", sequencesTotal >= 1000 && sequencesTotal <= 10000);
  countryMatchBars.classList.toggle("scale-4", sequencesTotal >= 10000 && sequencesTotal <= 100000);
  countryMatchBars.classList.toggle("scale-5", sequencesTotal > 100000);


  // Country match percents

  let countryMatchPercentValue = card.querySelector(".country--match--percent--value");
  if (sequencesInRange === 0) {
    countryMatchPercentValue.innerHTML = "0";
  } else {
    countryMatchPercentValue.innerHTML = `${ pct(sequencesInRange) }`;
  }

  let countryMatchPercentText = card.querySelector(".country--match--percent--text");

  // let countryMatchPercentLabel = `appear `;
  let countryMatchPercentLabel = 'in view';
  countryMatchPercentText.innerHTML = countryMatchPercentLabel;



  // Handle dates that aren't fully formed

  //let notFullyFormedDates = d3.max(data, d => d.notFullyFormedDates);

  //<span class="country--match--not-fully-formed-dates--value">0</span><span class="country--match--not-fully-formed-dates--text">% in view</span>

  // card.querySelector(".country--match--not-fully-formed-dates--value").innerHTML = notFullyFormedDates.toLocaleString();
  //
  // card.querySelector(".country--match--not-fully-formed-dates--text").innerHTML = `without dates`;

  // card.querySelector(".country--match--filtered--value").innerHTML = sequencesFiltered.toLocaleString();

  // Country match fill

  let countryMatchPercentFill = card.querySelector(".country--match--percent--fill");
  countryMatchPercentFill.style.width = `${ sequencesInRange * 100 }%`;


  // Stash some measurements for reuse above, can only be done after card is in the DOM
  //let cs = getComputedStyle(card);
  //let padding = parseInt(cs.getPropertyValue('padding-bottom'));

  //lineHeight = card.offsetHeight - labelsDiv.offsetHeight - padding - 5; // fudge factor



}

function getTickIntervals(x) {
  let ticks;
  let tf = getTimeframe();

  let width = x.range()[1] - x.range()[0];

  if (tf === '2weeks') {
    ticks = x.ticks(d3.timeDay.every(1));

    if (width < 500) {
      ticks = ticks.filter((t, i) => {
        return i % 2 === 0;
      });
    }

  } else if (tf === '4weeks') {
    ticks = x.ticks(d3.timeWeek.every(1));

    if (width < 500) {
      ticks = ticks.filter((t, i) => {
        return i % 2 === 0;
      });
    }

  } else if (tf === '4months') {
    ticks = x.ticks(d3.timeWeek.every(1));
    if (width < 500) {
      ticks = ticks.filter((t, i) => {
        return i % 2 === 0;
      });
    }

  } else if (tf === '1year') {
    ticks = x.ticks(d3.timeMonth.every(1));
  } else if (tf === 'all') {

    if (width < 500) {
      ticks = x.ticks(d3.timeMonth.every(2));
    } else {
      ticks = x.ticks(d3.timeMonth.every(1));
    }

  } else { // custom

    let startDate = DateTime.fromISO(tf.start);
    let endDate = DateTime.fromISO(tf.end);
    let diff = endDate.diff(startDate, 'days');
    let days = diff.days;


    if (days < 31) {
      ticks = x.ticks(d3.timeDay.every(1));

      if (width < 500) {
        ticks = ticks.filter((t, i) => {
          return i % 2 === 0;
        });
      }
    } else if (days < 180) {
      ticks = x.ticks(d3.timeWeek.every(1));

      if (width < 500 && days > 90) {
        ticks = ticks.filter((t, i) => {
          return i % 2 === 0;
        });
      }
    } else {
      if (width < 500 && days > 365) {
        ticks = x.ticks(d3.timeMonth.every(2));
      } else {
        ticks = x.ticks(d3.timeMonth.every(1));
      }
    }
  }

  return ticks;
}

function getIntervalLabel(date, tf, showYear, forceDate) {
  let label = ``;

  //if (forceDate || tf === '2weeks' || tf === '4weeks' || tf === '4months') {
  if (forceDate || getFrequency() === "daily") {

    label = `
      <label class="interval--month">
        ${ date.toFormat(CHART_DATE_FORMAT_MONTH_SHORT) }
      </label>
      <label class="interval--date">
        ${ date.toFormat(CHART_DATE_FORMAT_DATE_SHORT) }
      </label>`;

  //} else if (tf === '1year' || tf === 'all') {
  } else if (getFrequency() === "weekly") {

    label = `
      <label class="interval--month">
        ${ date.toFormat(CHART_DATE_FORMAT_MONTH_SHORT) }
      </label>`;

    if (showYear || date.get('month') === 1) { // First entry or January
      label += `<label class="interval--year">
          ${ date.toFormat(CHART_DATE_FORMAT_YEAR_SHORT) }
        </label>`;
    }
  }

  return label;
}


function handleError(err) {
  console.log(err);
  console.log(err.message);
  // document.body.classList.toggle("error", true);
  // document.body.classList.toggle("loading", false);
}
function clearError() {
  document.body.classList.toggle("error", false);
}



let trackEvent = (category, action, name, value) => {

  let _paq = window._paq = window._paq || [];
  _paq.push(['trackEvent', category, action, name, value]);

}
